import { useEffect, useMemo, useRef, useState } from 'react'
import Add from '@mui/icons-material/Add'
import { Button, Skeleton, Stack, Typography } from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import useOverlay from '@/hooks/useOverlay'
import {
  useCreateRollupConfig,
  useDeleteRollupConfig,
  useUpdateRollupConfig,
} from '@/service-library/hooks/rollup-configs'
import { Organization } from '@/types/organizations'
import { Project } from '@/types/projects'
import {
  RollupConfig,
  RollupConfigVendor,
  VendorExclusion,
} from '@/types/rollup'
import {
  computeClaimedVendorNumsByConfigId,
  computeVendorRequiredConfigIds,
  groupRollupConfigEntries,
  RollupConfigEntry,
} from '@/utils/rollup-config-grouping'
import { toggleRollupKeyInfoType } from '@/utils/rollup-key-dependency'
import { showErrorSnackbar } from '@/utils/snackbars'
import generateUuid from '@/utils/uuid'
import NewRollupConfigDialog from './NewRollupConfigDialog'
import RollupConfigCard from './RollupConfigCard'
import RollupEmptyState from './RollupEmptyState'
import useRollupKeyDependencies from './useRollupKeyDependencies'
import useRollupKeyFields from './useRollupKeyFields'

const NO_VENDOR_NUMS = new Set<string>()

/** A config's server-side vendor state as of the moment a save started —
 * see `savingByConfigId` for why the live cache can't be trusted mid-flight. */
type ServerVendorSnapshot = {
  isVendorlessOnServer: boolean
  serverVendorNums: Set<string>
}

type ConfigsTabProps = {
  project: Project
  dataProviderOrgs: Organization[]
  vendorExclusions: VendorExclusion[]
  rollupConfigs: RollupConfig[]
  rollupConfigsQueryKey: QueryKey
  isPending: boolean
}

export default function ConfigsTab({
  project,
  dataProviderOrgs,
  vendorExclusions,
  rollupConfigs,
  rollupConfigsQueryKey,
  isPending,
}: ConfigsTabProps) {
  const newConfigOverlay = useOverlay()

  // Drafts are never persisted until "Save config" — see the state model
  // notes on the story. They're local-only and intentionally lost on
  // navigation.
  const [drafts, setDrafts] = useState<RollupConfig[]>([])
  // Staged vendor edits for saved configs, keyed by config id. A config with
  // an entry here is "dirty" and only reaches the server on "Save config".
  const [editedVendorsById, setEditedVendorsById] = useState<
    Record<string, RollupConfigVendor[]>
  >({})
  // Staged grouping-key edits for saved configs, keyed by config id — same
  // shape and lifecycle as editedVendorsById above (dirty until Save,
  // reverted on Discard, cleared once the sent value round-trips).
  const [editedKeyInfoTypesById, setEditedKeyInfoTypesById] = useState<
    Record<string, string[]>
  >({})
  // Configs with a save PATCH in flight, each holding the server-side vendor
  // view captured just *before* the request went out. Two jobs:
  //
  // 1. Lock that card's controls. Discarding mid-flight can't recall the
  //    request, so it would clear the staged edit while the server goes on
  //    to apply it — the user is told it was discarded and it wasn't.
  // 2. Preserve "All Vendors" slot ownership across the flight.
  //    `useUpdateDetail` writes the outgoing item into the list cache
  //    optimistically, so `rollupConfigs` stops describing the server the
  //    moment a save starts. Without the snapshot, a config saving its first
  //    vendor would appear to free the slot while the request filling it is
  //    still on the wire, and a sibling could be saved into the same slot.
  //
  // Keyed per config rather than using the mutation's shared `isPending`,
  // which would freeze every card on the page while any one of them saves.
  const [savingByConfigId, setSavingByConfigId] = useState<
    Map<string, ServerVendorSnapshot>
  >(() => new Map())
  // The handlers close over render-time state, so a second click arriving
  // before React commits would read a stale set. The ref is the authority
  // for the guards; the state above only drives rendering.
  const savingRef = useRef<Map<string, ServerVendorSnapshot>>(new Map())
  // Set only on draft creation. By id, not position — the sort can drop a new
  // card mid-list.
  const [pendingScrollConfigId, setPendingScrollConfigId] = useState<
    string | null
  >(null)

  const { keyFields, isPending: isPendingKeyFields } = useRollupKeyFields()
  const { requiredAncestorsByInfoTypeId, isPending: isPendingKeyDependencies } =
    useRollupKeyDependencies(keyFields)

  const companyNameById = useMemo(() => {
    const map = new Map<string, string>()
    dataProviderOrgs.forEach((org) => map.set(org.id, org.name))
    return map
  }, [dataProviderOrgs])

  const excludedVendorNumsByCompany = useMemo(() => {
    const map = new Map<string, Set<string>>()
    vendorExclusions.forEach((exclusion) => {
      let excluded = map.get(exclusion.dp_org_id)
      if (!excluded) {
        excluded = new Set()
        map.set(exclusion.dp_org_id, excluded)
      }
      excluded.add(exclusion.vendor_num)
    })
    return map
  }, [vendorExclusions])

  // Oldest-first: saved configs in API (creation) order, then drafts
  // appended in the order they were created. Saved configs get their staged
  // vendor edit substituted in so every downstream computation (grouping,
  // vendor-required, claimed vendors, rendering) works off one "effective"
  // view without needing to know about edits separately.
  const entries = useMemo<RollupConfigEntry[]>(() => {
    const savedEntries: RollupConfigEntry[] = rollupConfigs.map((config) => {
      const editedVendors = editedVendorsById[config.id]
      const editedKeyInfoTypes = editedKeyInfoTypesById[config.id]
      // Mid-save the cache row is already the optimistic post-save value, so
      // the snapshot taken before the request went out is what still
      // describes the server. See `savingByConfigId`.
      const saving = savingByConfigId.get(config.id)
      return {
        config: {
          ...config,
          ...(editedVendors ? { vendors: editedVendors } : null),
          ...(editedKeyInfoTypes
            ? { key_info_types: editedKeyInfoTypes }
            : null),
        },
        isDraft: false,
        // Derived from the untouched server row (`config` here, before the
        // edit above is substituted in) — see the field's doc comment for
        // why that distinction matters.
        isVendorlessOnServer:
          saving?.isVendorlessOnServer ?? config.vendors.length === 0,
        serverVendorNums:
          saving?.serverVendorNums ??
          new Set(config.vendors.map((vendor) => vendor.vendor_num)),
      }
    })
    const draftEntries: RollupConfigEntry[] = drafts.map((config) => ({
      config,
      isDraft: true,
      isVendorlessOnServer: false,
      serverVendorNums: new Set<string>(),
    }))
    return [...savedEntries, ...draftEntries]
  }, [
    drafts,
    editedKeyInfoTypesById,
    editedVendorsById,
    rollupConfigs,
    savingByConfigId,
  ])

  const vendorRequiredConfigIds = useMemo(
    () => computeVendorRequiredConfigIds(entries),
    [entries],
  )

  const groups = useMemo(
    () => groupRollupConfigEntries(entries, companyNameById),
    [companyNameById, entries],
  )

  const hasAllCompaniesConfig = entries.some(
    (entry) => entry.config.dp_org_id === null,
  )

  const clearEditedVendors = (configId: string) => {
    setEditedVendorsById((prev) => {
      if (!(configId in prev)) return prev
      const next = { ...prev }
      delete next[configId]
      return next
    })
  }

  const clearEditedKeyInfoTypes = (configId: string) => {
    setEditedKeyInfoTypesById((prev) => {
      if (!(configId in prev)) return prev
      const next = { ...prev }
      delete next[configId]
      return next
    })
  }

  const { createRollupConfig } = useCreateRollupConfig({
    listQueryKey: rollupConfigsQueryKey,
    onError: (_error, item) => {
      showErrorSnackbar('Unable to save rollup config. Please try again later.')
      // The optimistic list entry was already rolled back — bring the draft
      // back so the user doesn't lose their work and can retry.
      setDrafts((prev) =>
        prev.some((draft) => draft.id === item.id) ? prev : [...prev, item],
      )
    },
  })

  const { updateRollupConfig, updateRollupConfigAsync } = useUpdateRollupConfig(
    {
      listQueryKey: rollupConfigsQueryKey,
      onError: () => {
        showErrorSnackbar(
          'Unable to save rollup config. Please try again later.',
        )
      },
    },
  )

  const { deleteRollupConfig } = useDeleteRollupConfig({
    listQueryKey: rollupConfigsQueryKey,
    // Only on success — on failure useDeleteDetail restores the row, and the
    // staged edit should still be there to restore with it.
    onSuccess: (_data, configId: string) => {
      clearEditedVendors(configId)
      clearEditedKeyInfoTypes(configId)
    },
    onError: () => {
      showErrorSnackbar(
        'Unable to delete rollup config. Please try again later.',
      )
    },
  })

  /** Claims the in-flight slot for a config. Returns false if one is already
   * in flight, which is the real double-submit guard. */
  const beginSave = (configId: string, snapshot: ServerVendorSnapshot) => {
    if (savingRef.current.has(configId)) return false
    const next = new Map(savingRef.current)
    next.set(configId, snapshot)
    savingRef.current = next
    setSavingByConfigId(next)
    return true
  }

  const endSave = (configId: string) => {
    if (!savingRef.current.has(configId)) return
    const next = new Map(savingRef.current)
    next.delete(configId)
    savingRef.current = next
    setSavingByConfigId(next)
  }

  // An effect, not the card's ref: refs fire before paint, while the dialog
  // that created the card is still closing.
  useEffect(() => {
    if (!pendingScrollConfigId) return
    document
      .querySelector(`[data-rollup-config-id="${pendingScrollConfigId}"]`)
      ?.scrollIntoView({ block: 'center' })
    setPendingScrollConfigId(null)
  }, [pendingScrollConfigId])

  const handleCreateDraft = (dpOrgId: string | null) => {
    const configId = generateUuid()
    setPendingScrollConfigId(configId)
    setDrafts((prev) => [
      ...prev,
      {
        id: configId,
        project_id: project.id,
        dp_org_id: dpOrgId,
        is_enabled: true,
        // Most conservative default key: everything eligible is kept.
        key_info_types: keyFields.map((field) => field.infoTypeId),
        vendors: [],
      },
    ])
  }

  const handleToggleEnabled = (entry: RollupConfigEntry) => {
    if (entry.isDraft) {
      setDrafts((prev) =>
        prev.map((draft) =>
          draft.id === entry.config.id
            ? { ...draft, is_enabled: !draft.is_enabled }
            : draft,
        ),
      )
      return
    }
    // Mid-save the cache row is the optimistic post-save value, so toggling
    // now would PATCH that speculative state back and race the request
    // already in flight for this same config.
    if (savingRef.current.has(entry.config.id)) return
    // Based on the untouched server copy (not the working/edited copy) so
    // this never carries a staged vendor edit the user hasn't saved yet —
    // only is_enabled changes.
    const serverConfig = rollupConfigs.find((c) => c.id === entry.config.id)
    if (!serverConfig) return
    updateRollupConfig({
      ...serverConfig,
      is_enabled: !serverConfig.is_enabled,
    })
  }

  const handleAddVendor = (
    entry: RollupConfigEntry,
    vendor: RollupConfigVendor,
  ) => {
    if (entry.isDraft) {
      setDrafts((prev) =>
        prev.map((draft) =>
          draft.id === entry.config.id
            ? { ...draft, vendors: [...draft.vendors, vendor] }
            : draft,
        ),
      )
      return
    }
    setEditedVendorsById((prev) => ({
      ...prev,
      [entry.config.id]: [...entry.config.vendors, vendor],
    }))
  }

  const handleRemoveVendor = (entry: RollupConfigEntry, vendorNum: string) => {
    if (entry.isDraft) {
      setDrafts((prev) =>
        prev.map((draft) =>
          draft.id === entry.config.id
            ? {
                ...draft,
                vendors: draft.vendors.filter(
                  (vendor) => vendor.vendor_num !== vendorNum,
                ),
              }
            : draft,
        ),
      )
      return
    }
    setEditedVendorsById((prev) => ({
      ...prev,
      [entry.config.id]: entry.config.vendors.filter(
        (vendor) => vendor.vendor_num !== vendorNum,
      ),
    }))
  }

  const handleToggleKeyInfoType = (
    entry: RollupConfigEntry,
    infoTypeId: string,
  ) => {
    const nextKeyInfoTypes = toggleRollupKeyInfoType(
      entry.config.key_info_types,
      infoTypeId,
      requiredAncestorsByInfoTypeId,
    )
    if (entry.isDraft) {
      setDrafts((prev) =>
        prev.map((draft) =>
          draft.id === entry.config.id
            ? { ...draft, key_info_types: nextKeyInfoTypes }
            : draft,
        ),
      )
      return
    }
    setEditedKeyInfoTypesById((prev) => ({
      ...prev,
      [entry.config.id]: nextKeyInfoTypes,
    }))
  }

  const handleSave = (entry: RollupConfigEntry) => {
    if (entry.isDraft) {
      setDrafts((prev) => prev.filter((draft) => draft.id !== entry.config.id))
      // Nothing to do on completion — onError re-adds the draft and shows the
      // snackbar, and the optimistic insert already rendered the saved card.
      createRollupConfig(entry.config)
      return
    }
    const configId = entry.config.id
    // Claims the in-flight slot, capturing the server-side vendor view before
    // the optimistic cache write lands. Declines if a save is already going.
    const started = beginSave(configId, {
      isVendorlessOnServer: entry.isVendorlessOnServer,
      serverVendorNums: entry.serverVendorNums,
    })
    if (!started) return
    // Snapshot the exact array references being sent. If the user stages a
    // further edit (e.g. adds a vendor, or toggles a key field) while this
    // PATCH is in flight, editedVendorsById/editedKeyInfoTypesById will hold
    // *different* array references by the time it resolves — clearing then
    // would silently discard that newer edit, so only clear if it's still
    // the one we sent.
    const sentVendors = editedVendorsById[configId]
    const sentKeyInfoTypes = editedKeyInfoTypesById[configId]
    updateRollupConfigAsync(entry.config)
      .then(() => {
        setEditedVendorsById((prev) => {
          if (prev[configId] !== sentVendors) return prev
          const next = { ...prev }
          delete next[configId]
          return next
        })
        setEditedKeyInfoTypesById((prev) => {
          if (prev[configId] !== sentKeyInfoTypes) return prev
          const next = { ...prev }
          delete next[configId]
          return next
        })
      })
      .catch(() => {
        // Stays dirty — onError above already rolled back the cache and
        // showed the snackbar, and the staged edit is left in place so the
        // user can retry.
      })
      .finally(() => endSave(configId))
  }

  const handleDiscard = (entry: RollupConfigEntry) => {
    if (entry.isDraft) {
      setDrafts((prev) => prev.filter((draft) => draft.id !== entry.config.id))
      return
    }
    // Discarding mid-flight can't un-send the PATCH, so it would clear the
    // staged edit while the server still applies it.
    if (savingRef.current.has(entry.config.id)) return
    clearEditedVendors(entry.config.id)
    clearEditedKeyInfoTypes(entry.config.id)
  }

  const handleDelete = (entry: RollupConfigEntry) => {
    if (entry.isDraft) {
      setDrafts((prev) => prev.filter((draft) => draft.id !== entry.config.id))
      return
    }
    // The staged edit is cleared in the hook's onSuccess above.
    deleteRollupConfig(entry.config.id)
  }

  if (isPending || isPendingKeyFields || isPendingKeyDependencies) {
    return (
      <Stack spacing={1.5}>
        <Skeleton variant="rounded" height={48} />
        <Skeleton variant="rounded" height={48} />
      </Stack>
    )
  }

  return (
    <Stack spacing={3}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 480 }}
        >
          Each config targets a company and its vendors, and defines how line
          items will be rolled up for export. If there isn&apos;t an applicable
          config for a vendor&apos;s invoice, the line items are not rolled up.
          If any line items on the invoice are matched to a purchase order, none
          of the line items are rolled up.
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={newConfigOverlay.open}
          sx={{ flexShrink: 0 }}
        >
          New rollup config
        </Button>
      </Stack>

      {groups.length === 0 ? (
        <RollupEmptyState>
          No rollup configs. Create one to configure how line items get rolled
          up on export.{' '}
          <Button variant="text" size="small" onClick={newConfigOverlay.open}>
            Create your first config
          </Button>
        </RollupEmptyState>
      ) : (
        <Stack spacing={3}>
          {groups.map((group) => {
            const claimedVendorNumsByConfigId =
              computeClaimedVendorNumsByConfigId(group.entries)

            return (
              <Stack key={group.dpOrgId ?? ''} spacing={1.5}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', fontWeight: 600 }}
                >
                  {group.companyName}
                </Typography>
                <Stack spacing={2}>
                  {group.entries.map((entry) => (
                    // RollupConfigCard forwards no ref, so this wrapper is the
                    // scroll anchor.
                    <div
                      key={entry.config.id}
                      data-rollup-config-id={entry.config.id}
                    >
                      <RollupConfigCard
                        config={entry.config}
                        companyName={group.companyName}
                        project={project}
                        isDraft={entry.isDraft}
                        isDirty={
                          !entry.isDraft &&
                          (entry.config.id in editedVendorsById ||
                            entry.config.id in editedKeyInfoTypesById)
                        }
                        isVendorRequired={vendorRequiredConfigIds.has(
                          entry.config.id,
                        )}
                        isSaving={savingByConfigId.has(entry.config.id)}
                        keyFields={keyFields}
                        requiredAncestorsByInfoTypeId={
                          requiredAncestorsByInfoTypeId
                        }
                        // Live example starts expanded with 1-2 configs on
                        // the page, collapsed once there are more.
                        defaultLiveExampleOpen={entries.length <= 2}
                        excludedVendorNumbers={
                          entry.config.dp_org_id
                            ? excludedVendorNumsByCompany.get(
                                entry.config.dp_org_id,
                              ) || NO_VENDOR_NUMS
                            : NO_VENDOR_NUMS
                        }
                        claimedVendorNumbers={
                          claimedVendorNumsByConfigId.get(entry.config.id) ||
                          NO_VENDOR_NUMS
                        }
                        onToggleEnabled={() => handleToggleEnabled(entry)}
                        onAddVendor={(vendor) => handleAddVendor(entry, vendor)}
                        onRemoveVendor={(vendorNum) =>
                          handleRemoveVendor(entry, vendorNum)
                        }
                        onToggleKeyInfoType={(infoTypeId) =>
                          handleToggleKeyInfoType(entry, infoTypeId)
                        }
                        onSave={() => handleSave(entry)}
                        onDiscard={() => handleDiscard(entry)}
                        onDelete={() => handleDelete(entry)}
                      />
                    </div>
                  ))}
                </Stack>
              </Stack>
            )
          })}
        </Stack>
      )}

      <NewRollupConfigDialog
        overlay={newConfigOverlay}
        project={project}
        dataProviderOrgs={dataProviderOrgs}
        hasAllCompaniesConfig={hasAllCompaniesConfig}
        onCreate={handleCreateDraft}
      />
    </Stack>
  )
}
